import { ImageName } from "@mcleod/images";
import { LogManager, HorizontalAlignment, CompanySettings } from "@mcleod/core";
import { SelectionEvent } from "../../events/SelectionEvent";
import { Label } from "../label/Label";
import { LabelProps } from "../label/LabelProps";
import { List } from "../list/List";
import { ListItem } from "../list/ListItem";
import { Table } from "./Table";
import { TableColumn } from "./TableColumn";
import { TableToolAnalytics, Category } from "../../databinding/Categorize";
import { Panel } from "../panel/Panel";
import { Snackbar } from "../../page/Snackbar";
import { ReflectiveDialogs } from "../../base/ReflectiveDialogs";
import { Textbox } from "../textbox/Textbox";
import { ChangeEvent } from "../../events/ChangeEvent";
import { select } from "d3-selection";
import { scaleOrdinal, scaleLinear } from "d3-scale";
import { max } from "d3-array";
import { HistogramChart } from "./visualizations/HistogramChart";
import { CategorySunburst } from "./visualizations/CategorySunburst";
import { CategoryTreemap } from "./visualizations/CategoryTreemap";

const log = LogManager.getLogger("components.table.TableToolsAnalyticsOptions");

export class TableToolsAnalyticsOptions {
    private table: Table;
    private commonLabelProps: Partial<LabelProps>;
    private labelTotal: Label;
    private labelCategories: Label;
    private labelCategoriesAuto: Label;
    private labelCategoriesSelectFields: Label;
    private originalCategories: Category[] | null = null;
    private fieldToCaptionMap: Map<string, string> = new Map();

    constructor(table: Table, commonProps: Partial<LabelProps>) {
        this.table = table;
        this.commonLabelProps = commonProps;
        this.setupLabels();
    }

    /**
     * Builds a mapping from field names to their user-friendly captions from table columns.
     * This allows us to display readable names instead of technical field names.
     */
    private async buildFieldToCaptionMap(): Promise<void> {
        this.fieldToCaptionMap.clear();

        if (!this.table.columns) {
            return;
        }

        for (const column of this.table.columns) {
            try {
                // Get the caption from the heading cell
                const caption = column.headingCell?.caption;

                if (caption) {
                    // Get all fields used in this column
                    const fields = await column.getFields();

                    // Map each field to this caption
                    for (const field of fields) {
                        if (field && !this.fieldToCaptionMap.has(field)) {
                            this.fieldToCaptionMap.set(field, caption);
                        }
                    }
                }

                // Also check extraSortFields which may have their own captions
                if (column.headingCell?.resolvedExtraSortFields) {
                    for (const sortFieldInfo of column.headingCell.resolvedExtraSortFields) {
                        if (sortFieldInfo.field && !this.fieldToCaptionMap.has(sortFieldInfo.field)) {
                            // Use the sortFieldInfo's caption if available, otherwise use the column caption
                            const fieldCaption = sortFieldInfo.caption || caption || sortFieldInfo.field;
                            this.fieldToCaptionMap.set(sortFieldInfo.field, fieldCaption);
                        }
                    }
                }
            } catch (error) {
                log.debug("Error processing column for field mapping:", error);
            }
        }

        log.debug("Built field to caption map with", this.fieldToCaptionMap.size, "entries");
    }

    /**
     * Gets a user-friendly caption for a field name.
     * Returns the caption if found, otherwise returns the field name formatted nicely.
     */
    private getFieldCaption(fieldName: string): string {
        if (!fieldName) {
            return "";
        }

        // Check if we have a mapped caption
        const caption = this.fieldToCaptionMap.get(fieldName);
        if (caption) {
            return caption;
        }

        // Fall back to formatting the field name
        // Convert snake_case to Title Case
        return fieldName
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    }

    async getDropdownAnalyticsItem(): Promise<ListItem> {
        if (!this.table.dataSource.lastSearch) {
            return null;
        }

        // Build the field to caption mapping first so we can populate the Total submenu
        await this.buildFieldToCaptionMap();

        const labelAnalytics = new Label({
            text: "Totals",
            fillRow: true,
            rowBreak: false,
            imageName: ImageName.list,
            ...this.commonLabelProps
        });

        // Create children for Total option - one for each field caption
        const totalChildren: ListItem[] = [];

        // Get unique captions from the map (values), sorted alphabetically
        const uniqueCaptions = Array.from(new Set(this.fieldToCaptionMap.values())).sort();

        for (const caption of uniqueCaptions) {
            // Find the field name(s) for this caption
            const fieldNames = Array.from(this.fieldToCaptionMap.entries())
                .filter(([_, c]) => c === caption)
                .map(([f, _]) => f);

            const fieldLabel = new Label({
                ...this.commonLabelProps,
                text: caption,
                imageName: ImageName.number,
                paddingRight: 8
            });

            const fieldItem = new ListItem(fieldLabel, null, null);
            totalChildren.push(fieldItem);
        }

        // Create the Total list item with its children and a handler to capture field selections
        const totalItem = new ListItem(this.labelTotal, totalChildren, async (event: SelectionEvent) => {
            console.log("Total item clicked, target:", event.target);

            if (event.domEvent.defaultPrevented === true) {
                console.log("Event was prevented");
                return;
            }

            // Get the selected item from the List
            const selectedItem = (event.target as List).selectedItem;
            console.log("Selected field item:", selectedItem);

            // Check if a field was selected (not the Total label itself)
            if (selectedItem instanceof Label && selectedItem !== this.labelTotal) {
                console.log("Total field selected:", selectedItem.text);
                const caption = selectedItem.text;

                // Find the field name for this caption
                const fieldNames = Array.from(this.fieldToCaptionMap.entries())
                    .filter(([_, c]) => c === caption)
                    .map(([f, _]) => f);

                if (fieldNames.length > 0) {
                    const fieldName = fieldNames[0];
                    console.log("Field name:", fieldName);
                    await this.runTotal(fieldName, caption);
                }
            }
        });

        let categoriesChildren = [];
        const aiConfigured = CompanySettings.get()?.ai_api_configured;
        if (aiConfigured) {
            categoriesChildren = [
                new ListItem(this.labelCategoriesAuto, null, null),
                new ListItem(this.labelCategoriesSelectFields, null, null)
            ];
        } else {
            categoriesChildren = [
                new ListItem(this.labelCategoriesSelectFields, null, null)
            ];
        }
        // Create the Categories list item with Auto and Select Fields children

        const categoriesItem = new ListItem(this.labelCategories, categoriesChildren, async (event: SelectionEvent) => {
            console.log("Categories item clicked, target:", event.target);

            if (event.domEvent.defaultPrevented === true) {
                console.log("Event was prevented");
                return;
            }

            // Get the selected item from the List
            const selectedItem = (event.target as List).selectedItem;
            console.log("Selected category option:", selectedItem);

            // Check which option was selected
            if (selectedItem === this.labelCategoriesAuto) {
                console.log("Auto categorization selected");
                await this.runCategorization();
            } else if (selectedItem === this.labelCategoriesSelectFields) {
                console.log("Select Fields categorization selected");
                await this.showSelectFieldsDialog();
            }
        });

        const children = [totalItem, categoriesItem];

        // Create the parent analyticsItem
        const analyticsItem = new ListItem(labelAnalytics, children, null);

        return analyticsItem;
    }

    private setupLabels() {
        this.labelTotal = new Label({
            ...this.commonLabelProps,
            id: "labelAnalyticsTotal",
            text: "Total",
            imageName: ImageName.calculator,
            paddingRight: 8
        });

        this.labelCategories = new Label({
            ...this.commonLabelProps,
            id: "labelAnalyticsCategories",
            text: "Categories",
            imageName: ImageName.folder,
            paddingRight: 8
        });

        this.labelCategoriesAuto = new Label({
            ...this.commonLabelProps,
            id: "labelAnalyticsCategoriesAuto",
            text: "Auto",
            imageName: ImageName.arrowRefresh,
            paddingRight: 8
        });

        this.labelCategoriesSelectFields = new Label({
            ...this.commonLabelProps,
            id: "labelAnalyticsCategoriesSelectFields",
            text: "Select Fields",
            imageName: ImageName.list,
            paddingRight: 8
        });
    }

    private async runCategorization(categoryFields?: string[]) {
        console.log("==> runCategorization() called", categoryFields ? "with custom fields: " + categoryFields : "(auto)");
        try {
            log.info("Starting categorization for table:", this.table.id);

            if (!this.table.dataSource.lastSearch) {
                Snackbar.showWarningSnackbar("Please perform a search before running categorization");
                return;
            }

            if (!this.table.dataSource.rowCount || this.table.dataSource.rowCount === 0) {
                Snackbar.showWarningSnackbar("Cannot run categorization: table has no data");
                return;
            }

            console.log("Building field to caption map...");
            // Build the field to caption mapping first
            await this.buildFieldToCaptionMap();
            console.log("Field map built, getting categories...");

            const analytics = new TableToolAnalytics(this.table.dataSource);
            let categories: Category[];

            if (categoryFields && categoryFields.length > 0) {
                // Use custom fields
                categories = await analytics.getCategoriesWithFields(categoryFields);
            } else {
                // Use automatic categorization
                categories = await analytics.getCategories();
            }

            console.log("Categories received:", categories);
            this.originalCategories = categories;

            log.info("Categorization complete. Results:", categories);
            console.log("=== Categorization Results ===");
            console.log("Total top-level categories:", categories.length);
            if (log.isDebugEnabled()) {
                this.logCategories(categories, 0);
                console.log("==============================");
            }

            // Create and display the category visualization panel
            console.log("Creating visualization panel...");
            const visualizationPanel = this.createCategoryVisualizationPanel(categories, categories);

            const title = categoryFields && categoryFields.length > 0
                ? `Category Analysis (Custom Fields)`
                : `Category Analysis`;

            console.log("Showing dialog...");
            ReflectiveDialogs.showDialog(visualizationPanel, {
                title: title,
                width: 1200,
                height: 1000
            });
            console.log("Dialog shown");

        } catch (error) {
            log.error("Error running categorization:", error);
            console.error("Categorization error:", error);
        }
    }

    private async showSelectFieldsDialog() {
        console.log("==> showSelectFieldsDialog() called");
        try {
            if (!this.table.dataSource.lastSearch) {
                Snackbar.showWarningSnackbar("Please perform a search before running categorization");
                return;
            }

            if (!this.table.dataSource.rowCount || this.table.dataSource.rowCount === 0) {
                Snackbar.showWarningSnackbar("Cannot run categorization: table has no data");
                return;
            }

            // Build the field to caption mapping first
            await this.buildFieldToCaptionMap();

            // Get available fields
            const availableFields = Array.from(this.fieldToCaptionMap.entries())
                .map(([fieldName, caption]) => ({ fieldName, caption }))
                .sort((a, b) => a.caption.localeCompare(b.caption));

            const fieldCaptions = availableFields.map(f => f.caption);

            // Create dialog panel
            const dialogPanel = new Panel({
                fillRow: true,
                padding: 16,
                backgroundColor: "background"
            });

            // Add instruction label
            const instructionLabel = new Label({
                text: "Select up to 4 fields to categorize by. Fields can be left blank.",
                fillRow: true,
                paddingBottom: 16,
                fontSize: "medium"
            });
            dialogPanel.add(instructionLabel);

            // Create 4 field selector textboxes
            const level1Field = new Textbox({
                caption: "Level 1",
                items: fieldCaptions,
                allowDropdownBlank: true,
                fillRow: true,
                paddingBottom: 8
            });
            dialogPanel.add(level1Field);

            const level2Field = new Textbox({
                caption: "Level 2",
                items: fieldCaptions,
                allowDropdownBlank: true,
                fillRow: true,
                paddingBottom: 8
            });
            dialogPanel.add(level2Field);

            const level3Field = new Textbox({
                caption: "Level 3",
                items: fieldCaptions,
                allowDropdownBlank: true,
                fillRow: true,
                paddingBottom: 8
            });
            dialogPanel.add(level3Field);

            const level4Field = new Textbox({
                caption: "Level 4",
                items: fieldCaptions,
                allowDropdownBlank: true,
                fillRow: true,
                paddingBottom: 8
            });
            dialogPanel.add(level4Field);

            // Show dialog and wait for user response
            const result = await ReflectiveDialogs.showDialog(dialogPanel, {
                title: "Select Category Fields",
                width: 500,
                height: 550,
                okButton: true,
                cancelButton: true
            });

            // If user clicked OK, process the selected fields
            if ( ! result.wasCancelled) {
                const selectedCaptions = [
                    level1Field.text,
                    level2Field.text,
                    level3Field.text,
                    level4Field.text
                ].filter(caption => caption && caption.trim() !== "");

                // Convert captions back to field names
                const selectedFieldNames = selectedCaptions
                    .map(caption => {
                        const field = availableFields.find(f => f.caption === caption);
                        return field ? field.fieldName : null;
                    })
                    .filter(fieldName => fieldName !== null) as string[];

                if (selectedFieldNames.length === 0) {
                    Snackbar.showWarningSnackbar("Please select at least one field");
                    return;
                }

                console.log("Selected fields:", selectedFieldNames);
                await this.runCategorization(selectedFieldNames);
            }

        } catch (error) {
            log.error("Error showing select fields dialog:", error);
            console.error("Select fields dialog error:", error);
        }
    }

    private async runTotal(fieldName: string, caption: string) {
        try {
            log.info("Calculating total for field:", fieldName);

            if (!this.table.dataSource.rowCount || this.table.dataSource.rowCount === 0) {
                Snackbar.showWarningSnackbar("Cannot calculate total: table has no data");
                return;
            }

            if (!this.table.dataSource.lastSearch) {
                Snackbar.showWarningSnackbar("Please perform a search before calculating totals");
                return;
            }

            const analytics = new TableToolAnalytics(this.table.dataSource);
            const total = await analytics.getTotal(fieldName, caption);

            log.info("Total calculation complete. Result:", total);

            // Create and display the total result panel
            const resultPanel = this.createTotalResultPanel(total);

            ReflectiveDialogs.showDialog(resultPanel, {
                title: `Statistics: ${caption}`,
                width: 900,
                height: 800
            });

        } catch (error) {
            log.error("Error calculating total:", error);
            console.error("Total calculation error:", error);
        }
    }

    private createTotalResultPanel(total: any): Panel {
        const containerPanel = new Panel({
            fillRow: true,
            fillHeight: true,
            padding: 0,
            backgroundColor: "background"
        });

        // If total is an array of objects, render it as a histogram bar chart
        if (Array.isArray(total) && total.length > 0) {
            // Create display type selector
            const displaySelector = new Textbox({
                caption: "Display Type",
                items: ["Chart", "Table"],
                text: "Chart",
                width: 200,
                allowDropdownBlank: false,
                padding: 8
            });

            containerPanel.add(displaySelector);

            const histogramContainer = document.createElement("div");
            histogramContainer.id = "histogram-container";
            histogramContainer.style.width = "100%";
            histogramContainer.style.height = "100%";
            histogramContainer.style.overflow = "auto";
            histogramContainer.style.padding = "16px";

            // Create table for data
            const dataTable = new Table({ fillRow: true, headerVisible: false, padding: 16, maxHeight: 700, visible: false });
            dataTable.addColumn(new TableColumn({
                headingDef: { caption: "Category", paddingLeft: 4 },
                cell: { field: "name" }
            }));
            dataTable.addColumn(new TableColumn({
                headingDef: { caption: "Count", width: 100, align: HorizontalAlignment.RIGHT },
                cell: { field: "itemCount", fillRow: true, align: HorizontalAlignment.RIGHT, paddingRight: 12 }
            }));

            // Add rows to the table
            for (const cat of total) {
                dataTable.addRow({
                    name: cat.name,
                    itemCount: cat.itemCount
                }, null, { display: true, addToData: false });
            }

            containerPanel.add(dataTable);

            // Handle display type changes
            displaySelector.addChangeListener((event: ChangeEvent) => {
                const displayType = event.newValue;
                if (displayType === "Chart") {
                    histogramContainer.style.display = "block";
                    dataTable.visible = false;
                } else {
                    histogramContainer.style.display = "none";
                    dataTable.visible = true;
                }
            });

            setTimeout(() => {
                const panelElement = containerPanel.element;
                if (panelElement) {
                    panelElement.appendChild(histogramContainer);
                    this.renderHistogramBarChart(histogramContainer, total);
                }
            }, 0);
        } else {
            // Only show stats panel if there's no histogram
            const statsPanel = new Panel({
                fillRow: true,
                padding: 16,
                backgroundColor: "background"
            });

            const totalLabel = new Label({
                text: `Total: ${total.sum.toLocaleString()}`,
                fontSize: "large",
                fillRow: true,
                paddingBottom: 8
            });

            const averageLabel = new Label({
                text: `Average: ${total.average.toLocaleString()}`,
                fontSize: "medium",
                fillRow: true,
                paddingBottom: 8
            });

            const maximumLabel = new Label({
                text: `Maximum: ${total.max.toLocaleString()}`,
                fontSize: "medium",
                fillRow: true,
                paddingBottom: 8
            });

            const minimumLabel = new Label({
                text: `Minimum: ${total.min.toLocaleString()}`,
                fontSize: "medium",
                fillRow: true,
                paddingBottom: 8
            });

            const recordsLabel = new Label({
                text: `Records used: ${total.count.toLocaleString()} of ${total.total_count.toLocaleString()}`,
                fontSize: "medium",
                fillRow: true
            });

            statsPanel.add(totalLabel);
            statsPanel.add(averageLabel);
            statsPanel.add(maximumLabel);
            statsPanel.add(minimumLabel);
            statsPanel.add(recordsLabel);

            containerPanel.add(statsPanel);
        }

        return containerPanel;
    }

    private renderHistogramBarChart(container: HTMLElement, categories: Category[]): void {
        const chart = new HistogramChart();
        chart.render(container, categories, (cat) => this.formatCategoryTooltip(cat));
    }

    private createCategoryVisualizationPanel(categories: Category[], rootCategories: Category[]): Panel {
        const containerPanel = new Panel({
            fillRow: true,
            fillHeight: true,
            padding: 0,
            backgroundColor: "background"
        });

        if (categories.length === 0) {
            containerPanel.add(new Label({
                text: "No categories found",
                fontSize: "large",
                color: "subtle"
            }));
            return containerPanel;
        }

        // Create visualization type selector
        const visualizationSelector = new Textbox({
            caption: "Visualization Type",
            items: ["Bar", "Sunburst", "Treemap", "Table"],
            allowDropdownBlank: false,
            text: "Bar",
            width: 200,
            padding: 8
        });

        containerPanel.add(visualizationSelector);

        // Create label to display current category field name
        const categoryFieldLabel = new Label({
            text: this.getCurrentCategoryFieldName(categories),
            paddingLeft: 16,
            paddingTop: 8,
            fontSize: "medium",
            color: "primary"
        });

        containerPanel.add(categoryFieldLabel);

        // Create a container div for the D3 visualization
        const svgContainer = document.createElement("div");
        svgContainer.id = "d3-visualization-container";
        svgContainer.style.width = "100%";
        svgContainer.style.height = "100%";
        svgContainer.style.overflow = "auto";

        // Create table for data
        const dataTable = this.createCategoryTable(categories);
        dataTable.visible = false;
        containerPanel.add(dataTable);

        // Handle visualization type changes
        visualizationSelector.addChangeListener((event: ChangeEvent) => {
            const visualizationType = event.newValue;
            if (visualizationType === "Table") {
                svgContainer.style.display = "none";
                categoryFieldLabel.visible = false;
                dataTable.visible = true;
            } else {
                svgContainer.style.display = "block";
                categoryFieldLabel.visible = true;
                dataTable.visible = false;
                svgContainer.innerHTML = "";
                if (visualizationType === "Bar") {
                    this.renderBarChart(svgContainer, categories, containerPanel, rootCategories, categoryFieldLabel);
                } else if (visualizationType === "Treemap") {
                    this.renderTreemap(svgContainer, categories, containerPanel, rootCategories, categoryFieldLabel);
                } else {
                    this.renderSunburst(svgContainer, categories, containerPanel, rootCategories, categoryFieldLabel);
                }
            }
        });

        // We need to add the div to the panel after it's rendered
        setTimeout(() => {
            const panelElement = containerPanel.element;
            if (panelElement) {
                panelElement.appendChild(svgContainer);
                // Default to bar chart
                this.renderBarChart(svgContainer, categories, containerPanel, rootCategories, categoryFieldLabel);
            }
        }, 0);

        return containerPanel;
    }

    private createCategoryTable(categories: Category[]): Table {
        // Extract all field names from the category hierarchy
        const fieldNames = this.extractFieldNamesFromCategories(categories);

        // Create table with dynamic columns based on category fields
        const dataTable = new Table({
            fillRow: true,
            headerVisible: true,
            padding: 16,
            maxHeight: 700
        });

        // Add columns for each category field
        fieldNames.forEach((fieldName, index) => {
            const caption = this.getFieldCaption(fieldName);
            dataTable.addColumn(new TableColumn({
                headingDef: { caption: caption, paddingLeft: 4 },
                cell: { field: `field_${index}`, paddingLeft: 4 }
            }));
        });

        // Add count column
        dataTable.addColumn(new TableColumn({
            headingDef: { caption: "Count", width: 100, align: HorizontalAlignment.RIGHT },
            cell: { field: "itemCount", fillRow: true, align: HorizontalAlignment.RIGHT, paddingRight: 12 }
        }));

        // Flatten categories into table rows - only include leaf nodes (most detailed rows)
        const flattenCategories = (cats: Category[], parentPath: string[] = []) => {
            cats.forEach(cat => {
                // Build the current path
                const currentPath = [...parentPath];
                if (cat.fieldValue !== null) {
                    currentPath.push(cat.fieldValue);
                } else if (cat.name) {
                    currentPath.push(cat.name);
                }

                // Only add rows that don't have subcategories (leaf nodes)
                if (!cat.subcategories || cat.subcategories.length === 0) {
                    // Create row data
                    const rowData: any = {
                        itemCount: cat.itemCount
                    };

                    // Populate field columns with path values
                    currentPath.forEach((value, index) => {
                        rowData[`field_${index}`] = value;
                    });

                    // Fill remaining columns with empty strings
                    for (let i = currentPath.length; i < fieldNames.length; i++) {
                        rowData[`field_${i}`] = "";
                    }

                    // Add row to table
                    dataTable.addRow(rowData, null, { display: true, addToData: false });
                } else {
                    // Process subcategories recursively
                    flattenCategories(cat.subcategories, currentPath);
                }
            });
        };

        flattenCategories(categories);

        return dataTable;
    }

    private getTotalCategoryCount(category: Category): number {
        // If the category has no subcategories, return its own count
        if (!category.subcategories || category.subcategories.length === 0) {
            return category.itemCount;
        }
        // Otherwise, sum up all subcategory counts recursively
        return category.subcategories.reduce((sum, subcat) => sum + this.getTotalCategoryCount(subcat), 0);
    }

    private renderBarChart(container: HTMLElement, categories: Category[], containerPanel: Panel, rootCategories: Category[], categoryFieldLabel?: Label): void {
        const width = 850;
        const height = 650;
        // Increase top margin when showing subcategories to make room for reset button
        const hasResetButton = categories !== rootCategories;
        const margin = { top: hasResetButton ? 40 : 20, right: 100, bottom: 50, left: 200 };
        const barHeight = 25;
        const barPadding = 3;

        // Update the category field label
        if (categoryFieldLabel) {
            categoryFieldLabel.text = this.getCurrentCategoryFieldName(categories);
        }

        // Create SVG
        const svg = select(container)
            .append("svg")
            .attr("width", width)
            .attr("height", Math.max(height, categories.length * (barHeight + barPadding) + margin.top + margin.bottom))
            .style("font", "12px sans-serif")
            .style("background", "#fafafa");

        const g = svg.append("g")
            .attr("transform", `translate(${margin.left},${margin.top})`);

        // Create scales
        const maxValue = max(categories, (d: Category) => this.getTotalCategoryCount(d)) || 0;
        const xScale = scaleLinear()
            .domain([0, maxValue])
            .range([0, width - margin.left - margin.right]);

        // Create color scale
        const color = scaleOrdinal<string>()
            .domain(["0", "1", "2", "3", "4", "5"])
            .range(["#1f77b4", "#ff7f0e", "#2ca02c", "#d62728", "#9467bd", "#8c564b"]);

        // Determine depth for coloring
        const depth = categories[0]?.path ? categories[0].path.split(" > ").length - 1 : 0;

        // Create bars
        const bars = g.selectAll("g")
            .data(categories)
            .join("g")
            .attr("transform", (d: Category, i: number) => `translate(0,${i * (barHeight + barPadding)})`)
            .style("cursor", (d: Category) => (d.subcategories && d.subcategories.length > 0) ? "pointer" : "default")
            .on("click", (event: MouseEvent, d: Category) => {
                event.stopPropagation();
                console.log("Bar clicked:", d.name, "Has subcategories:", d.subcategories?.length);
                if (d.subcategories && d.subcategories.length > 0) {
                    container.innerHTML = "";
                    this.renderBarChart(container, d.subcategories, containerPanel, rootCategories, categoryFieldLabel);
                }
            });

        // Add rectangles
        bars.append("rect")
            .attr("x", 0)
            .attr("width", (d: Category) => xScale(this.getTotalCategoryCount(d)))
            .attr("height", barHeight)
            .attr("fill", color(depth.toString()))
            .attr("stroke", "#fff")
            .attr("stroke-width", 1)
            .style("fill-opacity", 0.8)
            .on("mouseenter", function(event: MouseEvent, d: Category) {
                if (d.subcategories && d.subcategories.length > 0) {
                    select(this).style("fill-opacity", 1);
                }
            })
            .on("mouseleave", function() {
                select(this).style("fill-opacity", 0.8);
            });

        // Add tooltips
        bars.append("title")
            .text((d: Category) => {
                const totalCount = this.getTotalCategoryCount(d);
                let tooltip = this.formatCategoryTooltip(d);
                tooltip += `Total Items: ${totalCount}`;
                if (d.subcategories && d.subcategories.length > 0) {
                    tooltip += `\nSubcategories: ${d.subcategories.length}`;
                }
                return tooltip;
            });

        // Add labels
        bars.append("text")
            .attr("x", -5)
            .attr("y", barHeight / 2)
            .attr("dy", "0.35em")
            .attr("text-anchor", "end")
            .style("fill", "#333")
            .style("font-size", "12px")
            .style("font-weight", "bold")
            .style("pointer-events", "none")
            .text((d: Category) => d.name);

        // Add count labels on bars
        bars.append("text")
            .attr("x", (d: Category) => xScale(this.getTotalCategoryCount(d)) + 5)
            .attr("y", barHeight / 2)
            .attr("dy", "0.35em")
            .style("fill", "#333")
            .style("font-size", "11px")
            .style("pointer-events", "none")
            .text((d: Category) => this.getTotalCategoryCount(d));

        // Add subcategory indicator
        bars.append("text")
            .filter((d: Category) => d.subcategories && d.subcategories.length > 0)
            .attr("x", (d: Category) => xScale(this.getTotalCategoryCount(d)) + 30)
            .attr("y", barHeight / 2)
            .attr("dy", "0.35em")
            .style("fill", "#666")
            .style("font-size", "10px")
            .style("pointer-events", "none")
            .text((d: Category) => `(${d.subcategories.length} sub)`);

        // Add reset button at the top
        if (categories !== rootCategories) {
            const resetButton = svg.append("g")
                .attr("transform", `translate(${margin.left},10)`)
                .style("cursor", "pointer")
                .on("click", () => {
                    container.innerHTML = "";
                    this.renderBarChart(container, rootCategories, containerPanel, rootCategories, categoryFieldLabel);
                });

            resetButton.append("rect")
                .attr("x", 0)
                .attr("y", 0)
                .attr("width", 60)
                .attr("height", 20)
                .attr("fill", "#fff")
                .attr("stroke", "#999")
                .attr("stroke-width", 1)
                .attr("rx", 3);

            resetButton.append("text")
                .attr("x", 30)
                .attr("y", 10)
                .attr("dy", "0.35em")
                .attr("text-anchor", "middle")
                .style("font-size", "11px")
                .style("fill", "#333")
                .style("pointer-events", "none")
                .text("Reset");
        }
    }

    private renderSunburst(container: HTMLElement, categories: Category[], containerPanel: Panel, rootCategories: Category[], categoryFieldLabel?: Label): void {
        const sunburst = new CategorySunburst();
        sunburst.render(
            container,
            categories,
            rootCategories,
            containerPanel,
            categoryFieldLabel,
            (cat) => this.formatCategoryTooltip(cat),
            (cats) => this.convertToHierarchy(cats),
            (cats) => this.getCurrentCategoryFieldName(cats)
        );
    }

    private renderTreemap(container: HTMLElement, categories: Category[], containerPanel: Panel, rootCategories: Category[], categoryFieldLabel?: Label): void {
        const treemap = new CategoryTreemap();
        treemap.render(
            container,
            categories,
            rootCategories,
            containerPanel,
            categoryFieldLabel,
            (cat) => this.formatCategoryTooltip(cat),
            (cats) => this.convertToHierarchy(cats),
            (cats) => this.getCurrentCategoryFieldName(cats)
        );
    }

    private getCurrentCategoryFieldName(categories: Category[]): string {
        if (!categories || categories.length === 0) {
            return "";
        }

        // Get the field name from the first category
        const fieldName = categories[0].fieldName;
        if (!fieldName) {
            return "Category";
        }

        // Get the user-friendly caption for this field
        const caption = this.getFieldCaption(fieldName);
        return `Field: ${caption}`;
    }

    private formatCategoryTooltip(category: Category): string {
        let tooltip = "";

        // Parse and reformat the path to use captions instead of field names
        if (category.path) {
            // Path format from server: "field1: value1 > field2: value2"
            const pathParts = category.path.split(" > ");
            const formattedParts = pathParts.map(part => {
                // Each part is "fieldName: value"
                const colonIndex = part.indexOf(": ");
                if (colonIndex > 0) {
                    const fieldName = part.substring(0, colonIndex);
                    const value = part.substring(colonIndex + 2);
                    const caption = this.getFieldCaption(fieldName);
                    return `${caption}: ${value}`;
                }
                return part;
            });
            tooltip = formattedParts.join(" > ") + "\n";
        } else if (category.name) {
            tooltip = category.name + "\n";
        }

        return tooltip;
    }

    private convertToHierarchy(categories: Category[]): any {
        const maxDepth = 5;
        // Create root node
        const root: any = {
            name: "All Categories",
            children: [],
            itemCount: categories.reduce((sum, cat) => sum + cat.itemCount, 0)
        };

        // Convert each category (starting at depth 1, since root is depth 0)
        for (const category of categories) {
            root.children.push(this.convertCategoryToNode(category, 1, maxDepth));
        }

        return root;
    }

    private convertCategoryToNode(category: Category, depth: number, maxDepth: number): any {
        const node: any = {
            name: category.name,
            value: category.itemCount,
            itemCount: category.itemCount,
            fieldName: category.fieldName,
            fieldValue: category.fieldValue,
            path: category.path,
            originalCategory: category
        };

        // Only add children if we haven't reached the max depth
        if (depth < maxDepth && category.subcategories && category.subcategories.length > 0) {
            node.children = category.subcategories.map(sub => this.convertCategoryToNode(sub, depth + 1, maxDepth));
        }

        return node;
    }

    private extractFieldNamesFromCategories(categories: Category[]): string[] {
        const fieldNames: string[] = [];

        // Recursively extract field names from the hierarchy
        const extractFromCategory = (category: Category) => {
            if (category.fieldName && !fieldNames.includes(category.fieldName)) {
                fieldNames.push(category.fieldName);
            }
            if (category.subcategories && category.subcategories.length > 0) {
                category.subcategories.forEach(sub => extractFromCategory(sub));
            }
        };

        categories.forEach(cat => extractFromCategory(cat));
        return fieldNames;
    }

    private logCategories(categories: Category[], depth: number) {
        const indent = "  ".repeat(depth);
        for (const category of categories) {
            console.log(`${indent}📁 ${category.name}`);
            console.log(`${indent}   Items: ${category.itemCount}`);
            if (category.fieldName) {
                console.log(`${indent}   Field: ${category.fieldName}`);
            }
            if (category.fieldValue) {
                console.log(`${indent}   Value: ${category.fieldValue}`);
            }
            if (category.subcategories && category.subcategories.length > 0) {
                console.log(`${indent}   Subcategories: ${category.subcategories.length}`);
                this.logCategories(category.subcategories, depth + 1);
            }
        }
    }
}
